import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, useStripe, useElements } from '@stripe/react-stripe-js';
import CardFieldsGroup from '../CardFieldsGroup';
import { supabase, getLocalUser } from '../supabase';
import { theme } from '../theme';
import AppNav from '../AppNav';
import { getSavedPaymentMethod, removeSavedPaymentMethod, getStripeConfig, savePaymentMethod, getPurchaseHistory } from '../api/atlast';

// PaymentMethodScreen (Aug 14, per Ali's report) — the standalone page
// Ali chose over folding a "remove card" link into the Dashboard
// header or skipping self-serve removal entirely: consent that's easy
// to give and hard to take back isn't real consent, so this needs to
// be a real, findable page, not an afterthought. Deliberately minimal
// for now (just the one saved card and a remove button) — the natural
// first piece of a real Settings/Account page this app doesn't have
// yet, not a one-off screen to throw away once one exists.
function PaymentMethodScreen() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState(null);
  const [savedMethod, setSavedMethod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState(null);
  const [justRemoved, setJustRemoved] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);
  // New (Sep 2, per Ali's report — Purchase History feature).
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  useEffect(() => {
    getStripeConfig()
      .then(config => {
        if (config.publishable_key) setStripePromise(loadStripe(config.publishable_key));
      })
      .catch(e => console.error('PaymentMethodScreen: failed to load Stripe config', e));
  }, []);

  useEffect(() => {
    (async () => {
      const { data: { user } } = await getLocalUser();
      if (!user) {
        navigate('/login');
        return;
      }
      setUserId(user.id);
      try {
        const result = await getSavedPaymentMethod(user.id);
        setSavedMethod(result.has_saved_card ? result : null);
      } catch (e) {
        console.error('PaymentMethodScreen: failed to load saved method', e);
        setError('Could not load your saved payment method. Try again in a moment.');
      } finally {
        setLoading(false);
      }
      // Best-effort, separate from the saved-card load above — a
      // failed history fetch shouldn't block the card-management
      // section (the more important part of this page) from working.
      try {
        const historyResult = await getPurchaseHistory(user.id);
        setHistory(historyResult.history || []);
      } catch (e) {
        console.error('PaymentMethodScreen: failed to load purchase history', e);
      } finally {
        setHistoryLoading(false);
      }
    })();
  }, [navigate]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    window.location.href = 'https://flyatlast.com';
  };

  const handleRemove = async () => {
    setRemoving(true);
    setError(null);
    try {
      await removeSavedPaymentMethod(userId);
      setSavedMethod(null);
      setJustRemoved(true);
    } catch (e) {
      console.error('PaymentMethodScreen: failed to remove saved method', e);
      setError(e?.response?.data?.detail || 'Could not remove your saved card. Try again.');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh' }}>
      {/* Nav bar added (Aug 18, per Ali's report) — this page was
          missing the standard nav bar every other page has, confirmed
          from a live screenshot. Matches Dashboard's nav bar exactly,
          including fixing the tagline's translucent color along the
          way — it was still referencing the old cream RGB values
          (253,246,236) rather than white, a small leftover from
          before tonight's palette switch. The standalone "← Back to
          Dashboard" button that used to sit at the top of the content
          is removed, not kept alongside this — DealScreen and
          FreezeDetailScreen already established this exact precedent
          (a bottom/duplicate back button was removed there for the
          same reason: the nav bar's own back link already covers it). */}
      <AppNav current="payments" />

      {/* Widened from 480px (Sep 8, per Ali's report — real layout
          bug, confirmed via screenshot: the saved-card box and the
          purchase-history table were both squeezed into a login-card
          width, leaving huge empty margins on both sides on any real
          screen). 480px suits a simple form/card, not a data table
          with Description/Status/Amount columns that benefits from
          real horizontal room. Reuses PinTrip's already-established
          820px .screen--wide convention rather than inventing a third
          width value for the app to carry. */}
      <div style={{ maxWidth: '820px', margin: '0 auto', padding: '28px 20px' }}>
      <h1 style={{ fontFamily: theme.fontDisplay, fontSize: '22px', fontWeight: 800, color: theme.textPrimary, marginBottom: '4px' }}>
        Payments
      </h1>
      <p style={{ fontSize: '13px', color: theme.textMuted, marginBottom: '24px' }}>
        The card saved for faster Price Watch checkouts, if you've chosen to save one.
      </p>

      {loading && (
        <p style={{ fontSize: '13px', color: theme.textMuted }}>Loading…</p>
      )}

      {!loading && justRemoved && (
        <div style={{ background: '#EAF1F8', border: '1px solid #DDE3E7', borderLeft: '6px solid #F4C11C', borderRadius: '16px', padding: '16px', marginBottom: '16px' }}>
          <p style={{ fontSize: '13px', color: theme.textPrimary, margin: 0 }}>
            Your saved card has been removed. You'll be asked to enter a card again next time you start a Price Watch.
          </p>
        </div>
      )}

      {!loading && !justRemoved && savedMethod && (
        <div style={{ background: theme.surface, border: '1px solid #DDE3E7', borderLeft: '6px solid #F4C11C', boxShadow: '0 2px 10px rgba(30,58,95,0.07)', borderRadius: '16px', overflow: 'hidden', padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '18px' }}>
            <div style={{ width: '40px', height: '28px', background: theme.structural, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.action, fontSize: '16px', flexShrink: 0 }} aria-hidden="true">💳</div>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: theme.textPrimary, fontFamily: theme.fontMono }}>
                {savedMethod.brand ? savedMethod.brand.charAt(0).toUpperCase() + savedMethod.brand.slice(1) : 'Card'} •••• {savedMethod.last4}
              </div>
              <div style={{ fontSize: '12px', color: theme.textMuted, marginTop: '2px' }}>
                Saved with your consent — used to speed up future Price Watch checkouts.
              </div>
            </div>
          </div>

          <button
            onClick={handleRemove}
            disabled={removing}
            style={{
              width: '100%', background: '#fff', color: theme.danger, border: `2px solid ${theme.danger}`,
              borderRadius: '12px', padding: '11px', fontWeight: 700, fontSize: '13px',
              cursor: removing ? 'default' : 'pointer', opacity: removing ? 0.6 : 1, fontFamily: theme.fontDisplay
            }}
          >
            {removing ? 'Removing…' : 'Remove saved card'}
          </button>
        </div>
      )}

      {!loading && !savedMethod && (
        stripePromise ? (
          <Elements stripe={stripePromise}>
            <AddCardForm
              userId={userId}
              onSaved={(result) => { setSavedMethod({ has_saved_card: true, brand: result.brand, last4: result.last4 }); setJustRemoved(false); }}
            />
          </Elements>
        ) : (
          <div style={{ background: theme.surface, border: '1px solid #DDE3E7', borderLeft: '6px solid #F4C11C', boxShadow: '0 2px 10px rgba(30,58,95,0.07)', borderRadius: '16px', overflow: 'hidden', padding: '24px', textAlign: 'center' }}>
            <p style={{ fontSize: '13px', color: theme.textMuted, margin: 0 }}>
              No card saved yet. You'll be offered the option to save one the next time you start a Price Watch.
            </p>
          </div>
        )
      )}

      {error && (
        <p style={{ fontSize: '12px', color: theme.danger, marginTop: '14px', fontWeight: 600 }}>{error}</p>
      )}

      {/* Purchase History (Sep 2, per Ali's report) — real money-
          movement record across both ways this app charges a card:
          pin-bundle purchases and Price Freeze fees. Deliberately on
          this page rather than a separate one — this is already the
          app's one real "billing" home, and a history list belongs
          right alongside the card it was charged to. */}
      <h2 style={{ fontFamily: theme.fontDisplay, fontSize: '16px', fontWeight: 800, color: theme.textPrimary, marginTop: '36px', marginBottom: '4px' }}>
        Purchase history
      </h2>
      <p style={{ fontSize: '12.5px', color: theme.textMuted, marginBottom: '16px' }}>
        Pin-bundle purchases and Price Watch fees, most recent first.
      </p>

      {historyLoading && (
        <p style={{ fontSize: '13px', color: theme.textMuted }}>Loading…</p>
      )}

      {!historyLoading && history.length === 0 && (
        <div style={{ background: theme.surface, border: '1px solid #DDE3E7', borderLeft: '6px solid #F4C11C', boxShadow: '0 2px 10px rgba(30,58,95,0.07)', borderRadius: '16px', overflow: 'hidden', padding: '20px', textAlign: 'center' }}>
          <p style={{ fontSize: '13px', color: theme.textMuted, margin: 0 }}>
            No purchases yet.
          </p>
        </div>
      )}

      {/* Column headers (Sep 2, per Ali's report) — added alongside a
          real restructure of each row below into the same 3-column
          grid (1fr / 90px / 100px), so these headers genuinely line
          up with real columns rather than just floating labels above
          content that doesn't match that shape. */}
      {!historyLoading && history.length > 0 && (
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 90px 100px', gap: '8px',
          padding: '0 16px 6px', fontSize: '12px', fontWeight: 700, color: theme.textMuted,
          textTransform: 'uppercase', letterSpacing: '0.04em'
        }}>
          <span>Description</span>
          <span style={{ textAlign: 'center' }}>Status</span>
          <span style={{ textAlign: 'right' }}>Amount</span>
        </div>
      )}

      {!historyLoading && history.length > 0 && (
        <div style={{ background: theme.surface, border: '1px solid #DDE3E7', borderLeft: '6px solid #F4C11C', boxShadow: '0 2px 10px rgba(30,58,95,0.07)', borderRadius: '16px', overflow: 'hidden' }}>
          {history.map((item, i) => {
            // Freeze-specific status reads as one of three real
            // outcomes; pin bundles only ever have one meaningful
            // state once charged (there's no "kept/refunded" concept
            // for a pin slot). mocked/failed also handled, since
            // Stripe isn't necessarily configured for every
            // environment this renders in.
            //
            // Upgraded to real pill badges (Sep 2, per Ali's report —
            // "any thoughts on making the presentation better") —
            // plain colored text made these three genuinely different
            // financial outcomes (money never moved / moved then came
            // back / moved and stayed) hard to tell apart at a glance.
            // A background tint reads faster than a text-color
            // difference alone, especially scanning a list quickly.
            let statusLabel = 'Charged';
            let statusColor = theme.textPrimary;
            let statusBg = 'rgba(43,38,32,0.08)';
            if (item.payment_status === 'mocked') {
              statusLabel = 'No charge'; statusColor = theme.textMuted; statusBg = 'rgba(90,107,120,0.12)';
            } else if (item.type === 'price_freeze') {
              // Checked BEFORE the freeze_status branches below (Sep 4,
              // per Ali's report — real gap found live: an evidence-
              // approved refund only ever updates payment_status, not
              // freeze_status, since "the tracked price held" (kept)
              // and "the fee got refunded anyway, because you proved
              // you paid more" are separate facts — a freeze that's
              // kept doesn't stop being kept just because the fee was
              // later refunded. Without this check, that case fell
              // through to the freeze_status==='kept' branch below and
              // showed a plain "Kept" badge with zero indication the
              // $9.99 had actually come back — the exact "does this
              // refund round show up in Payment History" gap this was
              // built to close. Distinct label from the Day-5
              // automatic "Refunded" (price rose) below, since these
              // are genuinely different reasons a refund happened and
              // conflating them would lose real information.
              if (item.payment_status === 'kept_then_refunded_by_evidence') { statusLabel = 'Refunded — you paid more'; statusColor = '#F4C11C'; statusBg = '#1E3A5F'; }
              else if (item.freeze_status === 'refunded') { statusLabel = 'You paid nothing'; statusColor = '#F4C11C'; statusBg = '#1E3A5F'; }
              else if (item.freeze_status === 'kept') { statusLabel = 'Fee charged'; statusColor = '#1E3A5F'; statusBg = '#F4C11C'; }
              else if (item.freeze_status === 'cancelled') { statusLabel = 'Cancelled'; statusColor = theme.textMuted; statusBg = 'rgba(90,107,120,0.12)'; }
              else { statusLabel = 'Active'; statusColor = '#8A6D00'; statusBg = 'rgba(244,193,28,0.22)'; }
            }

            // Hold-vs-charge clarification (Sep 14, per Ali's report —
            // real gap: "Active" next to a dollar amount reads as
            // already charged, when it's actually just an authorized
            // hold — nothing moves until the freeze resolves (charged
            // if kept, released if the price rises). Scoped to exactly
            // the 'Active' price-freeze state, since every other status
            // here already describes something that's actually
            // happened (Kept/Refunded/Cancelled all reflect a real,
            // completed outcome).
            // is_trial split out (Sep 24, per Ali's report — real bug:
            // a genuinely free trial (fee_amount 0, no Stripe intent
            // at all) was showing "Hold placed — not charged yet,"
            // which reads exactly like a real hold happened when it
            // never did — is_trial wasn't even in this row's data
            // until now, so there was no way to tell the two apart
            // here at all).
            const isActiveTrial = item.type === 'price_freeze' && statusLabel === 'Active' && item.is_trial;
            const isActiveHold = item.type === 'price_freeze' && statusLabel === 'Active' && !item.is_trial;

            // Time-of-day added alongside date (Sep 2, per Ali's
            // report) — several real entries in this list landed on
            // the exact same date from repeated same-day testing,
            // making otherwise-identical-looking rows impossible to
            // tell apart without it.
            const dateObj = new Date(item.created_at);
            const dateLabel = dateObj.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
            const timeLabel = dateObj.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

            return (
              <div
                key={i}
                style={{
                  padding: '14px 16px',
                  borderBottom: i < history.length - 1 ? `1px solid ${theme.bg}` : 'none',
                  display: 'grid', gridTemplateColumns: '1fr 90px 100px', gap: '8px', alignItems: 'center'
                }}
              >
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: theme.textPrimary }}>{item.description}</div>
                  <div style={{ fontSize: '12px', color: theme.textMuted, marginTop: '3px' }}>{dateLabel} · {timeLabel}</div>
                  {isActiveHold && (
                    <div style={{ fontSize: '12px', color: '#8A6D00', marginTop: '3px' }}>Hold placed — not charged yet</div>
                  )}
                  {isActiveTrial && (
                    <div style={{ fontSize: '12px', color: theme.structural, marginTop: '3px' }}>Free trial — never charged, once per account</div>
                  )}
                </div>
                <div style={{ textAlign: 'center' }}>
                  <span style={{
                    fontSize: '11.5px', fontWeight: 700, color: statusColor, background: statusBg,
                    padding: '2px 8px', borderRadius: '10px', whiteSpace: 'nowrap', display: 'inline-block'
                  }}>
                    {statusLabel}
                  </span>
                </div>
                <div style={{ fontFamily: theme.fontMono, fontWeight: 700, fontSize: '13.5px', color: theme.structural, whiteSpace: 'nowrap', textAlign: 'right' }}>
                  {/* Real bug found live (Sep 8, per Ali's report,
                      caught by directly comparing this page against
                      the actual Stripe transaction) — this hardcoded
                      amount_usd with a literal "USD" suffix on every
                      row, regardless of what currency the charge
                      actually happened in. The backend has sent the
                      real converted amount/currency correctly all
                      along (see get_purchase_history's own fee_amount/
                      currency, which come straight from
                      price_freezes.fee_amount/currency — the actual
                      pin-currency figures Stripe was charged in, per
                      create_freeze's fx.convert_amount call) — this
                      was the one place that ignored it. A EUR-pinned
                      freeze showed "$9.99 USD" instead of the real
                      "8.63 EUR", and every non-USD/CAD freeze had the
                      same mismatch, just less obviously wrong than a
                      currency swap makes it look. Now shows the real
                      amount/currency, no symbol — matching the plain
                      "amount CURRENCY_CODE" convention already used
                      everywhere else in the app (e.g. PinTrip's own
                      "Budget: 400 EUR"), not inventing a new one. */}
                  {Number(item.amount).toFixed(2)} {item.currency}
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
}

export default PaymentMethodScreen;

// AddCardForm — the real, standalone version of FreezePaymentModal's
// CardForm (Aug 18, per Ali's report). Same underlying security
// pattern: Stripe Elements renders the actual card number field in
// its own secure iframe, so raw card data never touches this app's
// own state or backend — stripe.createPaymentMethod() sends it
// straight to Stripe, and only the resulting safe paymentMethod.id
// comes back here to be handed to the real savePaymentMethod
// endpoint. No freeze-specific copy or fee context, since this page
// isn't part of that flow — just "save a card for next time."
function AddCardForm({ userId, onSaved }) {
  const [showForm, setShowForm] = useState(false);

  if (!showForm) {
    return (
      <div
        onClick={() => setShowForm(true)}
        style={{
          background: theme.surface, border: '1px solid #DDE3E7', borderLeft: '6px solid #F4C11C', boxShadow: '0 2px 10px rgba(30,58,95,0.07)', borderRadius: '16px', overflow: 'hidden',
          padding: '20px', textAlign: 'center', cursor: 'pointer'
        }}
      >
        <p style={{ fontSize: '13px', color: theme.textMuted, margin: '0 0 12px' }}>
          No card saved yet.
        </p>
        <span style={{
          display: 'inline-block', background: theme.action, color: '#1a1a1a', fontFamily: theme.fontDisplay,
          padding: '10px 20px', fontWeight: 800, fontSize: '13px'
        }}>
          + Add a card
        </span>
      </div>
    );
  }

  return <AddCardFormFields userId={userId} onSaved={onSaved} onCancel={() => setShowForm(false)} />;
}

// AddCardFormFields — the real Stripe Elements card entry, matching
// FreezePaymentModal's own CardForm security pattern exactly: the
// actual card number field renders in Stripe's own secure iframe, so
// raw card data never touches this app's state or backend —
// stripe.createPaymentMethod() sends it straight to Stripe, and only
// the resulting safe paymentMethod.id comes back here. No freeze-
// specific copy or fee context, since this page isn't part of that
// flow — just "save a card for next time." Cancel is a real action
// here (collapses back to the compact trigger above), not dropped —
// Ali specifically asked for Save/Cancel/Delete.
function AddCardFormFields({ userId, onSaved, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    if (!stripe || !elements || !consent) return;
    setSubmitting(true);
    setError(null);

    const cardElement = elements.getElement(CardNumberElement);
    const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    });

    if (pmError) {
      setError(pmError.message || 'Could not process this card. Check the details and try again.');
      setSubmitting(false);
      return;
    }

    try {
      const result = await savePaymentMethod(userId, paymentMethod.id, consent);
      onSaved(result);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not save this card. Try again.');
      setSubmitting(false);
    }
  };

  return (
    <div style={{ background: theme.surface, border: '1px solid #DDE3E7', borderLeft: '6px solid #F4C11C', boxShadow: '0 2px 10px rgba(30,58,95,0.07)', borderRadius: '16px', overflow: 'hidden', padding: '24px' }}>
      <p style={{ fontFamily: theme.fontDisplay, fontSize: '14px', fontWeight: 800, color: theme.textPrimary, marginBottom: '14px' }}>
        Add a card
      </p>

      <CardFieldsGroup />

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '18px' }}>
        <span style={{ fontSize: '12px', color: theme.textMuted, lineHeight: 1.5 }}>
          Payment secured by <b style={{ color: '#635BFF' }}>Stripe</b> — <b style={{ color: theme.textPrimary }}>FlyAtlast never sees or stores your card number.</b>
        </span>
      </div>

      {/* Layout bug found live (Sep 1, per Ali's report) — flagged
          directly from a screenshot: this label's text rendered in an
          extremely narrow, broken vertical column instead of wrapping
          normally next to the checkbox. Rebuilt with explicit flex
          sizing (checkbox at a fixed width, text taking the remaining
          space) rather than the previous flex:1/minWidth:0 combination
          on the span, which apparently wasn't enough on its own to
          keep this laid out correctly in every browser. */}
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '14px', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          style={{ marginTop: '2px', width: '16px', height: '16px', flexShrink: 0 }}
        />
        <span style={{ fontSize: '12.5px', color: theme.textMuted, lineHeight: 1.5, display: 'block', width: '100%' }}>
          I consent to saving this card for future Price Watch checkouts. I can remove it anytime.
        </span>
      </label>

      {/* Liability reminder (Aug 31, per Ali's report — Phase 5, item
          #6 from Ali's original 8-point list). Same text and same
          "plain text, not a link yet" reasoning as FreezePaymentModal's
          matching addition — see that file's comment for the full
          explanation. Shown here too since a card saved on this screen
          still sets up future Price Freeze charges, even without an
          immediate one happening right now. Draft text only — not
          reviewed by a lawyer. */}
      <p style={{ fontSize: '12px', color: theme.textMuted, lineHeight: 1.5, marginBottom: '18px' }}>
        Price Watch doesn't reserve the fare; it's about the US$5.99&ndash;US$9.99 fee you pay for it, depending on how long you watch for. Our responsibility is limited to this fee, as set out in our <a href="https://flyatlast.com/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "underline" }}>terms</a>.
      </p>

      {error && (
        <p style={{ fontSize: '12.5px', color: theme.danger, marginBottom: '12px', fontWeight: 600 }}>{error}</p>
      )}

      <div style={{ display: 'flex', gap: '10px' }}>
        <button
          onClick={handleSave}
          disabled={!stripe || !consent || submitting}
          style={{
            flex: 1, background: theme.action, color: '#1a1a1a', border: 'none', borderRadius: '12px',
            padding: '13px', fontWeight: 800, fontSize: '14px', fontFamily: theme.fontDisplay,
            cursor: (!consent || submitting) ? 'default' : 'pointer', opacity: (!consent || submitting) ? 0.6 : 1
          }}
        >
          {submitting ? 'Saving…' : 'Save card'}
        </button>
        <button
          onClick={onCancel}
          disabled={submitting}
          style={{
            flex: 1, background: '#fff', color: theme.textPrimary, border: `2px solid ${theme.border}`, borderRadius: '12px',
            padding: '13px', fontWeight: 700, fontSize: '14px', fontFamily: theme.fontDisplay,
            cursor: submitting ? 'default' : 'pointer'
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}